import { DeviceMobile, DeviceTablet, Watch, Laptop, Headphones, Desktop, GameController } from "@phosphor-icons/react";

const FORM_WORDS = ["phone", "smartphone", "tablet", "laptop", "notebook", "computer", "watch", "desktop", "console", "audio", "device"];

/** Clean a raw device_type string; drop it if it's just a form-factor word. */
function cleanType(type) {
  let t = (type || "").trim().replace(/\s+/g, " ");
  t = t.replace(/\s*series$/i, "").trim();
  if (!t) return "";
  if (FORM_WORDS.includes(t.toLowerCase())) return "";
  return t;
}

/** Derive the marketing device family/series from the model name. */
function deriveFamily(name) {
  const s = (name || "").toLowerCase();
  // Apple
  if (/iphone/.test(s)) return "iPhone";
  if (/ipad\s*pro/.test(s)) return "iPad Pro";
  if (/ipad\s*air/.test(s)) return "iPad Air";
  if (/ipad\s*mini/.test(s)) return "iPad Mini";
  if (/ipad/.test(s)) return "iPad";
  if (/macbook\s*air/.test(s)) return "MacBook Air";
  if (/macbook\s*pro/.test(s)) return "MacBook Pro";
  if (/macbook/.test(s)) return "MacBook";
  if (/imac/.test(s)) return "iMac";
  if (/mac\s*mini/.test(s)) return "Mac Mini";
  if (/mac\s*studio/.test(s)) return "Mac Studio";
  if (/apple\s*watch|\bwatch\b/.test(s)) return "Apple Watch";
  if (/airpods?\s*pro/.test(s)) return "AirPods Pro";
  if (/airpods?\s*max/.test(s)) return "AirPods Max";
  if (/airpods/.test(s)) return "AirPods";
  // Samsung
  if (/galaxy\s*z\s*fold/.test(s)) return "Galaxy Z Fold";
  if (/galaxy\s*z\s*flip/.test(s)) return "Galaxy Z Flip";
  if (/galaxy\s*note/.test(s)) return "Galaxy Note";
  if (/galaxy\s*tab/.test(s)) return "Galaxy Tab";
  if (/galaxy\s*watch/.test(s)) return "Galaxy Watch";
  if (/galaxy\s*buds/.test(s)) return "Galaxy Buds";
  if (/galaxy\s*s\d/.test(s) || /galaxy\s*s\b/.test(s)) return "Galaxy S";
  if (/galaxy\s*a\d/.test(s)) return "Galaxy A";
  if (/galaxy\s*m\d/.test(s)) return "Galaxy M";
  if (/galaxy\s*f\d/.test(s)) return "Galaxy F";
  if (/galaxy/.test(s)) return "Galaxy";
  // Google
  if (/pixel.*fold/.test(s)) return "Pixel Fold";
  if (/pixel.*tablet/.test(s)) return "Pixel Tablet";
  if (/pixel.*watch/.test(s)) return "Pixel Watch";
  if (/pixel/.test(s)) return "Pixel";
  if (/nest/.test(s)) return "Nest";
  // Others
  if (/oneplus/.test(s)) return "OnePlus";
  if (/redmi/.test(s)) return "Redmi";
  if (/poco/.test(s)) return "Poco";
  if (/xperia/.test(s)) return "Xperia";
  if (/\bmoto\b|motorola/.test(s)) return "Moto";
  if (/nokia/.test(s)) return "Nokia";
  if (/huawei/.test(s)) return "Huawei";
  if (/oppo/.test(s)) return "Oppo";
  if (/vivo/.test(s)) return "Vivo";
  if (/realme/.test(s)) return "Realme";
  if (/surface\s*pro/.test(s)) return "Surface Pro";
  if (/surface\s*laptop/.test(s)) return "Surface Laptop";
  if (/surface/.test(s)) return "Surface";
  if (/playstation|\bps[345]\b/.test(s)) return "PlayStation";
  if (/xbox/.test(s)) return "Xbox";
  if (/nintendo|switch/.test(s)) return "Nintendo";
  return "";
}

/** Derive the form-factor category (drives the icon only). */
function deriveCategory(name, type) {
  const hay = `${name} ${type || ""}`.toLowerCase();
  if (/ipad|tablet|\btab\b|galaxy tab|surface pro/.test(hay)) return "Tablet";
  if (/watch|band\b/.test(hay)) return "Watch";
  if (/macbook|laptop|notebook|surface (laptop|book)/.test(hay)) return "Laptop";
  if (/airpod|buds|headphone|earbud|earphone/.test(hay)) return "Audio";
  if (/imac|desktop|mac mini|mac studio/.test(hay)) return "Desktop";
  if (/console|playstation|\bps[345]\b|xbox|nintendo|switch/.test(hay)) return "Console";
  return "Phone";
}

/**
 * Parse the raw ticket device fields into a clean, display-ready shape.
 *
 * Real prod data is messy: `device_type` is empty on ~90% of tickets (and
 * occasionally wrong, e.g. an iPad tagged "Phone"), while `device_model`
 * has the brand, year and model-numbers all baked into one string. So we
 * normalise everything and *derive* the device family (iPad, Galaxy S,
 * MacBook, Pixel…) from the model text, only falling back to the form factor
 * when nothing better is known.
 */
export function parseDeviceInfo(make, type, model, year, modelNumber) {
  const brand = (make || "").trim();
  let raw = (model || "").trim();

  // Strip a leading brand prefix ("Samsung Galaxy S20" -> "Galaxy S20").
  if (brand && raw.toLowerCase().startsWith(brand.toLowerCase() + " ")) {
    raw = raw.slice(brand.length).trim();
  }

  // Peel apart " · " separated extras (year, model numbers).
  const parts = raw.split("·").map((s) => s.trim()).filter(Boolean);
  const name = parts[0] || raw;
  const extras = parts.slice(1);

  let extractedYear = (year || "").toString().trim();
  const modelNumbers = [];
  extras.forEach((p) => {
    if (/^(19|20)\d{2}$/.test(p)) {
      if (!extractedYear) extractedYear = p;
    } else {
      modelNumbers.push(p);
    }
  });
  if (modelNumber && String(modelNumber).trim()) modelNumbers.push(String(modelNumber).trim());

  const category = deriveCategory(name, type);
  // Family/series chip: prefer a meaningful captured device_type, else derive
  // from the model name, else fall back to the form-factor category.
  const family = cleanType(type) || deriveFamily(name) || category;

  return {
    brand,
    family,
    category,
    model: name || "Unknown Device",
    year: extractedYear,
    modelNumbers,
  };
}

// Per-brand accent styling for the chips + icon tile.
const BRAND_STYLES = {
  apple: { chipBg: "#111114", chipText: "#FFFFFF", tile: "#F2F2F4", icon: "#1D1D1F" },
  samsung: { chipBg: "#1428A0", chipText: "#FFFFFF", tile: "#EEF2FF", icon: "#1428A0" },
  google: { chipBg: "#1A73E8", chipText: "#FFFFFF", tile: "#EAF2FE", icon: "#1A73E8" },
  huawei: { chipBg: "#CF0A2C", chipText: "#FFFFFF", tile: "#FEECEF", icon: "#CF0A2C" },
  xiaomi: { chipBg: "#FF6900", chipText: "#FFFFFF", tile: "#FFF1E6", icon: "#FF6900" },
  oneplus: { chipBg: "#EB0029", chipText: "#FFFFFF", tile: "#FDECEE", icon: "#EB0029" },
  oppo: { chipBg: "#0B7A3B", chipText: "#FFFFFF", tile: "#E9F7EF", icon: "#0B7A3B" },
  motorola: { chipBg: "#5C2D91", chipText: "#FFFFFF", tile: "#F1EAFB", icon: "#5C2D91" },
  _default: { chipBg: "#121216", chipText: "#FFFFFF", tile: "#F1F5F9", icon: "#475569" },
};

const CATEGORY_ICON = {
  Phone: DeviceMobile,
  Tablet: DeviceTablet,
  Watch: Watch,
  Laptop: Laptop,
  Desktop: Desktop,
  Audio: Headphones,
  Console: GameController,
};

/**
 * Premium device header cluster — brand-tinted icon tile + clean model name +
 * brand / family / year chips. Used on the ticket page and the invoice modal
 * so both stay perfectly consistent.
 */
export function DeviceIdentity({ index, make, type, model, year, modelNumber, compact = false }) {
  const info = parseDeviceInfo(make, type, model, year, modelNumber);
  const bs = BRAND_STYLES[info.brand.toLowerCase()] || BRAND_STYLES._default;
  const Icon = CATEGORY_ICON[info.category] || DeviceMobile;

  const s = compact
    ? { tile: "w-9 h-9", icon: 18, title: "text-[13px]", chip: "text-[9px]" }
    : { tile: "w-11 h-11", icon: 22, title: "text-[15px]", chip: "text-[10px]" };

  return (
    <div className="flex items-center gap-3 min-w-0" data-testid="device-identity">
      <div
        className={`relative ${s.tile} rounded-xl flex items-center justify-center shrink-0`}
        style={{ background: bs.tile }}
      >
        <Icon size={s.icon} weight="duotone" style={{ color: bs.icon }} />
        {index != null && (
          <span className="absolute -top-1.5 -right-1.5 min-w-[16px] h-4 px-1 rounded-full bg-[#121216] text-white text-[9px] font-bold flex items-center justify-center ring-2 ring-white">
            {index}
          </span>
        )}
      </div>
      <div className="min-w-0">
        <div className={`${s.title} font-bold text-[#121216] truncate leading-tight`} data-testid="device-model">
          {info.model}
        </div>
        <div className="flex items-center gap-1.5 mt-1 flex-wrap">
          {info.brand && (
            <span
              className={`inline-flex items-center ${s.chip} font-bold uppercase tracking-[0.08em] px-1.5 py-0.5 rounded`}
              style={{ background: bs.chipBg, color: bs.chipText }}
              data-testid="device-brand-chip"
            >
              {info.brand}
            </span>
          )}
          <span
            className={`inline-flex items-center ${s.chip} font-bold uppercase tracking-[0.08em] px-1.5 py-0.5 rounded text-[#475569] bg-[#F1F5F9]`}
            data-testid="device-type-chip"
          >
            {info.family}
          </span>
          {info.year && (
            <span className={`inline-flex items-center ${s.chip} font-mono font-semibold px-1.5 py-0.5 rounded text-[#94A3B8] bg-[#F8FAFC] border border-[#EEF2F6]`}>
              {info.year}
            </span>
          )}
          {info.modelNumbers.slice(0, 1).map((mn, i) => (
            <span key={i} className={`hidden sm:inline-flex items-center ${s.chip} font-mono px-1.5 py-0.5 rounded text-[#94A3B8] bg-[#F8FAFC] border border-[#EEF2F6]`}>
              {mn}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

export default DeviceIdentity;
